import React, { useState, useEffect, useCallback } from "react";
import { format, subDays } from "date-fns";

import {
  makeStyles,
  Paper,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  CircularProgress,
  Card,
  CardContent,
  Grid,
  Box,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Collapse,
} from "@material-ui/core";
import RefreshIcon from "@material-ui/icons/Refresh";
import DeleteIcon from "@material-ui/icons/Delete";
import ErrorIcon from "@material-ui/icons/Error";
import WarningIcon from "@material-ui/icons/Warning";
import TodayIcon from "@material-ui/icons/Today";
import StorageIcon from "@material-ui/icons/Storage";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import ConfirmationModal from "../../components/ConfirmationModal";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(2),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
  kpiCard: {
    borderLeft: "4px solid",
  },
  kpiContent: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "12px 16px !important",
  },
  kpiValue: {
    fontSize: "1.5rem",
    fontWeight: 700,
  },
  kpiLabel: {
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
  },
  stackTrace: {
    backgroundColor: "#1e1e1e",
    color: "#d4d4d4",
    padding: theme.spacing(1.5),
    borderRadius: 4,
    fontSize: "0.75rem",
    fontFamily: "monospace",
    whiteSpace: "pre-wrap",
    wordBreak: "break-all",
    maxHeight: 300,
    overflowY: "auto",
  },
}));

const LEVEL_CONFIG = {
  error: { color: "#d32f2f", icon: ErrorIcon },
  warn: { color: "#ed6c02", icon: WarningIcon },
  info: { color: "#1976d2", icon: null },
};

const ErrorLogs = () => {
  const classes = useStyles();
  const [logs, setLogs] = useState([]);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [dateFrom, setDateFrom] = useState(format(subDays(new Date(), 7), "yyyy-MM-dd"));
  const [dateTo, setDateTo] = useState(format(new Date(), "yyyy-MM-dd"));
  const [levelFilter, setLevelFilter] = useState("");
  const [stats, setStats] = useState({ todayErrors: 0, totalErrors: 0, lastError: null });
  const [expandedRow, setExpandedRow] = useState(null);
  const [confirmClear, setConfirmClear] = useState(false);

  const fetchStats = useCallback(async () => {
    try {
      const { data } = await api.get("/error-logs/stats");
      setStats(data);
    } catch {}
  }, []);

  const fetchLogs = useCallback(async (pageNum) => {
    setLoading(true);
    try {
      const { data } = await api.get("/error-logs", {
        params: { pageNumber: pageNum + 1, dateFrom, dateTo, level: levelFilter },
      });
      setLogs(data.logs);
      setCount(data.count);
    } catch (err) {
      toastError(err);
    }
    setLoading(false);
  }, [dateFrom, dateTo, levelFilter]);

  useEffect(() => {
    fetchStats();
    fetchLogs(0);
  }, []);

  const handleSearch = () => {
    setPage(0);
    fetchLogs(0);
    fetchStats();
  };

  const handlePageChange = (e, newPage) => {
    setPage(newPage);
    fetchLogs(newPage);
  };

  const handleClearLogs = async () => {
    try {
      await api.delete("/error-logs");
      toast.success("Logs eliminados.");
      setLogs([]);
      setCount(0);
      fetchStats();
    } catch (err) {
      toastError(err);
    }
    setConfirmClear(false);
  };

  return (
    <MainContainer>
      <ConfirmationModal
        title="Eliminar todos los logs"
        open={confirmClear}
        onClose={setConfirmClear}
        onConfirm={handleClearLogs}
      >
        ¿Esta seguro que desea eliminar todos los logs de errores? Esta accion no se puede deshacer.
      </ConfirmationModal>
      <MainHeader>
        <Title>Logs de Errores</Title>
        <MainHeaderButtonsWrapper>
          <TextField label="Desde" type="date" value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            InputLabelProps={{ shrink: true }} variant="outlined" size="small" style={{ width: 155 }} />
          <TextField label="Hasta" type="date" value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            InputLabelProps={{ shrink: true }} variant="outlined" size="small" style={{ width: 155 }} />
          <FormControl variant="outlined" size="small" style={{ minWidth: 110 }}>
            <InputLabel>Nivel</InputLabel>
            <Select value={levelFilter} onChange={(e) => setLevelFilter(e.target.value)} label="Nivel">
              <MenuItem value="">Todos</MenuItem>
              <MenuItem value="error">Error</MenuItem>
              <MenuItem value="warn">Warning</MenuItem>
            </Select>
          </FormControl>
          <Button variant="contained" color="primary" size="small" onClick={handleSearch}
            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
            disabled={loading}>Buscar</Button>
          <Button variant="outlined" color="secondary" size="small" onClick={() => setConfirmClear(true)}
            startIcon={<DeleteIcon />}>Limpiar</Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>
      <Paper className={classes.mainPaper} variant="outlined">
        {/* KPIs */}
        <Grid container spacing={2} style={{ marginBottom: 16 }}>
          <Grid item xs={12} sm={4}>
            <Card className={classes.kpiCard} elevation={2} style={{ borderLeftColor: "#d32f2f" }}>
              <CardContent className={classes.kpiContent}>
                <div>
                  <Typography className={classes.kpiValue} style={{ color: "#d32f2f" }}>{stats.todayErrors}</Typography>
                  <Typography className={classes.kpiLabel}>Errores hoy</Typography>
                </div>
                <TodayIcon style={{ fontSize: 36, opacity: 0.2, color: "#d32f2f" }} />
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card className={classes.kpiCard} elevation={2} style={{ borderLeftColor: "#ed6c02" }}>
              <CardContent className={classes.kpiContent}>
                <div>
                  <Typography className={classes.kpiValue} style={{ color: "#ed6c02" }}>{stats.totalErrors}</Typography>
                  <Typography className={classes.kpiLabel}>Total acumulado</Typography>
                </div>
                <StorageIcon style={{ fontSize: 36, opacity: 0.2, color: "#ed6c02" }} />
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card className={classes.kpiCard} elevation={2} style={{ borderLeftColor: "#1976d2" }}>
              <CardContent className={classes.kpiContent}>
                <div>
                  <Typography className={classes.kpiLabel}>Ultimo error</Typography>
                  <Typography variant="body2" style={{ fontWeight: 500 }}>
                    {stats.lastError ? format(new Date(stats.lastError.createdAt), "dd/MM/yyyy HH:mm") : "Sin errores"}
                  </Typography>
                </div>
                <ErrorIcon style={{ fontSize: 36, opacity: 0.2, color: "#1976d2" }} />
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Table */}
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell style={{ width: 30 }}></TableCell>
                <TableCell>Fecha</TableCell>
                <TableCell>Nivel</TableCell>
                <TableCell>Mensaje</TableCell>
                <TableCell>Endpoint</TableCell>
                <TableCell>Metodo</TableCell>
                <TableCell>Codigo</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {logs.map((log) => (
                <React.Fragment key={log.id}>
                  <TableRow hover onClick={() => setExpandedRow(expandedRow === log.id ? null : log.id)}
                    style={{ cursor: "pointer" }}>
                    <TableCell>
                      <IconButton size="small">
                        {expandedRow === log.id ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                      </IconButton>
                    </TableCell>
                    <TableCell style={{ whiteSpace: "nowrap", fontSize: "0.8rem" }}>
                      {format(new Date(log.createdAt), "dd/MM/yyyy HH:mm:ss")}
                    </TableCell>
                    <TableCell>
                      <Chip label={log.level} size="small" style={{
                        backgroundColor: (LEVEL_CONFIG[log.level] || LEVEL_CONFIG.info).color,
                        color: "#fff", fontWeight: 600, fontSize: "0.7rem",
                      }} />
                    </TableCell>
                    <TableCell style={{ maxWidth: 350, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: "0.8rem" }}>
                      {log.message}
                    </TableCell>
                    <TableCell style={{ fontSize: "0.8rem", fontFamily: "monospace" }}>{log.endpoint || "-"}</TableCell>
                    <TableCell style={{ fontSize: "0.8rem" }}>{log.method || "-"}</TableCell>
                    <TableCell>
                      {log.statusCode && (
                        <Chip label={log.statusCode} size="small" variant="outlined"
                          style={{ fontWeight: 600, fontSize: "0.7rem",
                            borderColor: log.statusCode >= 500 ? "#d32f2f" : "#ed6c02",
                            color: log.statusCode >= 500 ? "#d32f2f" : "#ed6c02",
                          }} />
                      )}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={7} style={{ padding: 0, border: 0 }}>
                      <Collapse in={expandedRow === log.id} timeout="auto" unmountOnExit>
                        <Box p={2}>
                          <Typography variant="subtitle2" gutterBottom>Stack Trace:</Typography>
                          <div className={classes.stackTrace}>
                            {log.stack || "Sin stack trace disponible"}
                          </div>
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))}
              {!loading && logs.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center">Sin errores registrados</TableCell>
                </TableRow>
              )}
              {loading && (
                <TableRow>
                  <TableCell colSpan={7} align="center"><CircularProgress size={24} /></TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={count}
          page={page}
          rowsPerPage={50}
          rowsPerPageOptions={[50]}
          onPageChange={handlePageChange}
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
        />
      </Paper>
    </MainContainer>
  );
};

export default ErrorLogs;
